import { sortBy } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useDefaultAggregation } from '../../../providers/DefaultAggregationContext';
import { useFavoriteAggregations } from '../../../providers/FavoriteAggregationsContext';
import type { MarketSelectorContextProps } from '../MarketSelectorContext';
import { useMarketSelectorContext } from '../MarketSelectorContext';
import { CUSTOM_VALUE } from '../tokens';
import type { MarketSelectorCustomPresetItem } from '../types';
import type { PresetRowEntry } from './presetListModel';

const DEFAULT_CUSTOM_PRESET_ITEM: MarketSelectorCustomPresetItem = {
  id: CUSTOM_VALUE,
  label: 'Custom',
  onClick: api => api.unselectAll(),
  isSelected: 'fallback',
};

/** Derived list rows + selection for the market selector presets panel (reads Market / favorites / default aggregation contexts). */
export function useMarketSelectorPresets(): {
  marketSelectorAPI: MarketSelectorContextProps;
  defaultAggregationId: string | undefined;
  setDefaultAggregationId: (id: string | undefined) => void;
  favoriteAggregationsSet: ReadonlySet<string>;
  favoriteAggregation: (aggregationId: string, favorite: boolean) => void;
  customPresetItemsByID: Map<string, MarketSelectorCustomPresetItem>;
  showNoAggregations: boolean;
  listEntries: PresetRowEntry[];
  selectedPreset: string | undefined;
  selectedListEntry: PresetRowEntry | null;
  getLabel: (entry: PresetRowEntry) => string;
} {
  const marketSelectorAPI = useMarketSelectorContext();
  const { presets, preset, presetsByID, customPresetItems: inputCustomPresetItems } = marketSelectorAPI;

  const { defaultAggregationId, setDefaultAggregationId } = useDefaultAggregation();
  const { favoriteAggregationsSet, favoriteAggregation } = useFavoriteAggregations();

  const customPresetItems = useMemo(
    () => [DEFAULT_CUSTOM_PRESET_ITEM, ...sortBy(inputCustomPresetItems, item => item.label)],
    [inputCustomPresetItems]
  );

  const customPresetItemsByID = useMemo(
    () => new Map(customPresetItems.map(item => [item.id, item])),
    [customPresetItems]
  );

  const showNoAggregations = inputCustomPresetItems.length === 0 && presets != null && presets.length === 0;

  const resolvedDefaultAggregationPresetId = useMemo(() => {
    if (defaultAggregationId == null || customPresetItemsByID.has(defaultAggregationId)) {
      return undefined;
    }
    if (!presetsByID?.has(defaultAggregationId)) {
      return undefined;
    }
    return defaultAggregationId;
  }, [customPresetItemsByID, defaultAggregationId, presetsByID]);

  const listEntries: PresetRowEntry[] = useMemo(() => {
    if (showNoAggregations) {
      return [];
    }

    const pinEntries: PresetRowEntry[] =
      resolvedDefaultAggregationPresetId != null
        ? [
            {
              rowKey: `pin:${resolvedDefaultAggregationPresetId}`,
              kind: 'aggregation',
              presetId: resolvedDefaultAggregationPresetId,
              placement: 'defaultPin',
            },
          ]
        : [];

    const customEntries: PresetRowEntry[] = customPresetItems.map(item => ({
      rowKey: `custom:${item.id}`,
      kind: 'custom' as const,
      presetId: item.id,
    }));

    const sortedAggregationIds = sortBy(
      presets ?? [],
      p => !favoriteAggregationsSet.has(p.id),
      p => p.label.toLowerCase()
    ).map(p => p.id);

    const aggregationEntries: PresetRowEntry[] = sortedAggregationIds.map(id => ({
      rowKey: `list:${id}`,
      kind: 'aggregation' as const,
      presetId: id,
      placement: 'list' as const,
    }));

    return [...pinEntries, ...customEntries, ...aggregationEntries];
  }, [customPresetItems, favoriteAggregationsSet, presets, resolvedDefaultAggregationPresetId, showNoAggregations]);

  const selectedPreset: string | undefined = useMemo(() => {
    if (preset) {
      return preset;
    }

    const maybeFallbackItem = customPresetItems.find(item => item.isSelected === 'fallback');
    const maybeSelectedItem = customPresetItems.find(
      item => item.isSelected !== 'fallback' && item.isSelected(marketSelectorAPI)
    );
    return maybeSelectedItem?.id ?? maybeFallbackItem?.id;
  }, [preset, customPresetItems, marketSelectorAPI]);

  const selectedListEntry = useMemo((): PresetRowEntry | null => {
    if (selectedPreset == null || listEntries.length === 0) {
      return null;
    }
    if (customPresetItemsByID.has(selectedPreset)) {
      return listEntries.find(e => e.kind === 'custom' && e.presetId === selectedPreset) ?? null;
    }
    return (
      listEntries.find(e => e.kind === 'aggregation' && e.presetId === selectedPreset && e.placement === 'list') ?? null
    );
  }, [customPresetItemsByID, listEntries, selectedPreset]);

  const getPresetLabel = useCallback(
    (presetID: string) => {
      return customPresetItemsByID.get(presetID)?.label ?? presetsByID?.get(presetID)?.label ?? presetID;
    },
    [customPresetItemsByID, presetsByID]
  );

  const getLabel = useCallback((entry: PresetRowEntry) => getPresetLabel(entry.presetId), [getPresetLabel]);

  return {
    marketSelectorAPI,
    defaultAggregationId,
    setDefaultAggregationId,
    favoriteAggregationsSet,
    favoriteAggregation,
    customPresetItemsByID,
    showNoAggregations,
    listEntries,
    selectedPreset,
    selectedListEntry,
    getLabel,
  };
}
